import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy, Timestamp } from 'firebase/firestore';
import { useUser } from '../context/UserContext';
import { useLocale } from '../context/DynamicLocaleProvider';
import { db } from '../lib/firebase';

interface Document {
  id: string;
  documentId: string;
  title: string;
  sender: string;
  createdAt: Date;
  detectedDocumentCategory: string;
  status: string;
  taskCount: number;
  doneTaskCount: number;
  currency?: string;
  dueDate?: any;
  invoiceDate?: any;
  invoiceNumber?: string;
  paymentTerms?: string;
  receiver?: string;
  senderWebsiteUrl?: string;
  summary?: string;
  subtotal?: string;
  taxAmount?: string;
  totalAmount?: string;
  userId: string;
  content?: string;
  fileName?: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  companyId?: string;
  // Add other user fields as needed
}

export function useRoleBasedData() {
  const userProfile = useUser();
  const user = userProfile.user;
  const { t } = useLocale();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const role = userProfile?.role;

  // Fetch documents based on role
  const fetchDocuments = async () => {
    if (!user || !role) return;

    // Skip fetching documents for client_admin - they don't need invoices
    if (role === 'client_admin') {
      setDocuments([]);
      setLoadingDocuments(false);
      return;
    }

    setLoadingDocuments(true);
    setError(null);

    try {
      let documentsQuery;

      switch (role) {
        case 'internal_admin':
          // Internal admin sees ALL invoices
          documentsQuery = query(
            collection(db, 'invoices'),
            orderBy('createdAt', 'desc')
          );
          break;

        case 'family_member':
        default:
          // Family members see only their own invoices
          documentsQuery = query(
            collection(db, 'invoices'),
            where('userId', '==', user.uid)
          );
          break;
      }

      const documentsSnapshot = await getDocs(documentsQuery);
      
      const documentsData = documentsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          documentId: data.documentId || '',
          title: data.name || data.title || 'Untitled',
          sender: data.sender || 'Unknown',
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : 
                     data.creationDate instanceof Timestamp ? data.creationDate.toDate() : 
                     new Date(),
          detectedDocumentCategory: data.detectedDocumentCategory || 'Unknown',
          status: data.status || 'submitted',
          taskCount: data.taskCount || 0,
          doneTaskCount: data.doneTaskCount || 0,
          // Map additional fields
          currency: data.currency,
          dueDate: data.dueDate,
          invoiceDate: data.invoiceDate,
          invoiceNumber: data.invoiceNumber,
          paymentTerms: data.paymentTerms,
          receiver: data.receiver,
          senderWebsiteUrl: data.senderWebsiteUrl,
          summary: data.summary,
          subtotal: data.subtotal,
          taxAmount: data.taxAmount,
          totalAmount: data.totalAmount,
          userId: data.userId,
          content: data.content,
          fileName: data.filename || data.fileName, // Firestore uses 'filename' (lowercase)
        };
      }) as Document[];

      // Sort documents by creation date (newest first) since we removed orderBy from query
      documentsData.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

      setDocuments(documentsData);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error fetching documents:', err);
      }
      setError(err instanceof Error ? err.message : 'Failed to fetch documents');
    } finally {
      setLoadingDocuments(false);
    }
  };

  // Fetch users based on role
  const fetchUsers = async () => {
    if (!user || !role) return;

    setLoadingUsers(true);
    setError(null);

    try {
      let usersQuery;

      switch (role) {
        case 'internal_admin':
          // Internal admin sees ALL users
          // Note: Some users might not have createdAt field, so we'll fetch without orderBy first
          usersQuery = query(
            collection(db, 'users')
          );
          break;

        case 'client_admin':
          // Client admin sees users in their company
          if (!userProfile.company) {
            throw new Error('Client admin company not found in user profile');
          }
          usersQuery = query(
            collection(db, 'users'),
            where('company', '==', userProfile.company)
          );
          break;

        case 'family_member':
        default:
          // Family members see only their family members
          // This uses the existing familyMembers array from userProfile
          // Note: familyMembers might not be in userProfile, may need to fetch from user document
          setUsers([]);
          setLoadingUsers(false);
          return;
      }

      const usersSnapshot = await getDocs(usersQuery);
      
      const usersData = usersSnapshot.docs.map(doc => {
        const data = doc.data();
        
        // Construct name from firstName and lastName, fallback to name field
        const fullName = data.firstName && data.lastName 
          ? `${data.firstName} ${data.lastName}`
          : data.name || 'Unknown User';
        
        return {
          id: doc.id,
          name: fullName,
          email: data.email || '',
          role: data.role || '',
          companyId: data.companyId || data.company,
          // Add required fields for UserTile
          status: 'active' as const, // Default status
          ...data
        };
      }) as User[];

      setUsers(usersData);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error fetching users:', err);
      }
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
    } finally {
      setLoadingUsers(false);
    }
  };

  // Fetch data when component mounts or role changes
  useEffect(() => {
    if (user && role) {
      fetchDocuments();
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, role]);

  // Get appropriate tab labels based on role
  const getTabLabels = () => {
    switch (role) {
      case 'internal_admin':
      case 'client_admin':
        return {
          documents: t('home_tab_invoices'),
          users: t('home_tab_users')
        };
      case 'family_member':
      default:
        return {
          documents: t('home_tab_invoices'),
          users: t('home_tab_users')
        };
    }
  };

  return {
    documents,
    users,
    loadingDocuments,
    loadingUsers,
    error,
    refetchDocuments: fetchDocuments,
    refetchUsers: fetchUsers,
    tabLabels: getTabLabels()
  };
}
